import React, { useRef, useState } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import useAssortmentTestConfigs from '~/shared/utils/useAssortmentTestConfigs';
import { AssetContainer, ImageAsset } from '~/techstyle-shared/react-assets';
import {
  Carousel,
  mobile,
  desktop,
  useBreakpoint,
} from '~/techstyle-shared/react-components';

import { useStyleGuideContext } from '../../context/StyleGuideContext';
import useStyleGuideFilterData from '../../utils/useStyleGuideFilterData';
import StyleGuideFilterPills from '../StyleGuideFilterPills/StyleGuideFilterPills';

import StyleGuideAsset from './components/StyleGuideAsset';

const StyledWrapper = styled.div`
  display: grid;
  padding-top: 24px;
  ${desktop`
      display: grid;
      grid-template-columns: ${({ hasLeadAsset }) =>
        hasLeadAsset ? 'auto 1fr' : '1fr'};
      grid-gap: 28px;
  `};
`;

const StyledCarousel = styled(Carousel)`
  .flickity-slider img {
    max-width: none;
  }

  .flickity-button[disabled] {
    display: none;
  }

  .flickity-enabled:focus {
    outline: none;
  }

  .flickity-button:focus {
    outline: none;
    box-shadow: none;
  }

  ${desktop`
    .flickity-prev-next-button {
      &.previous {
        left: -25px;
      }

      &.next {
        right: -25px;
      }
    }
  `}

  ${mobile`
  .flickity-prev-next-button {
     width: 30px;
     height: 30px;
  }
`}
`;

const StyledAssetContainer = styled(AssetContainer)``;

const cellStyle = css`
  counter-increment: carousel-cell;
  width: max-content;
  margin-right: 8px;
`;

export default function StyleGuide({ containerName, leadAsset }) {
  const { isDesktop } = useBreakpoint();
  const { setFilterValues, tagList } = useStyleGuideContext();
  const { filterValues } = useStyleGuideFilterData(tagList);
  const [sortedFilterValues, setSortedFilterValues] = useState(false);
  const showFilterPills = filterValues?.length > 0 && isDesktop;
  const { excludeFplCodes, excludeTagIds } = useAssortmentTestConfigs();

  // flickity carousel dependencies
  const flickityRef = useRef();
  const carouselOptions = {
    contain: true,
    pageDots: false,
    resize: true,
    groupCells: true,
    cellStyle,
    freeScroll: true,
    wrapAround: false,
    autoPlay: false,
  };

  const handleOnLoad = (assets) => {
    if (!sortedFilterValues) {
      setSortedFilterValues(true);
      handleFilterPills(assets);
    }
    if (flickityRef?.current) {
      flickityRef.current.resize();
    }
  };

  const handleFilterPills = (assets) => {
    const pills = assets.map((asset) => {
      return asset.options?.customVars?.tag_name;
    });
    setFilterValues(pills);
  };

  const filterAssets = (assets) => {
    if (excludeFplCodes.length === 0 && excludeTagIds.length === 0) {
      return assets;
    }

    return assets.filter((asset) => {
      const assetFplExclusions = (
        asset.options?.customVars?.assortmentTestFplExclusions || ''
      ).split(',');
      const assetTagExclusions = (
        asset.options?.customVars?.assortmentTestTagExclusions || ''
      ).split(',');

      return (
        !excludeFplCodes.some((code) => assetFplExclusions.includes(code)) &&
        !excludeTagIds.some((id) => assetTagExclusions.includes(id))
      );
    });
  };

  return (
    <>
      <StyledWrapper hasLeadAsset={leadAsset}>
        {isDesktop && leadAsset && (
          <StyledAssetContainer name={leadAsset} multi>
            {({ data: assets, loading }) => {
              if (loading || !assets || !assets.length) {
                return null;
              }
              const filteredAssets = filterAssets(assets);
              if (filteredAssets.length === 0) {
                return null;
              }
              return filteredAssets.map((asset) => (
                <ImageAsset key={asset.id} asset={asset} />
              ));
            }}
          </StyledAssetContainer>
        )}
        <StyledAssetContainer name={containerName} multi>
          {({ data: assets, loading }) => {
            if (loading || !assets || !assets.length) {
              return null;
            }
            const filteredAssets = filterAssets(assets);
            if (filteredAssets.length === 0) {
              return null;
            }
            return (
              <StyledCarousel {...carouselOptions} flickityRef={flickityRef}>
                {filteredAssets.map((asset) => (
                  <StyleGuideAsset
                    key={asset.id}
                    asset={asset}
                    tagList={tagList}
                    handleOnLoad={() => handleOnLoad(filteredAssets)}
                  />
                ))}
              </StyledCarousel>
            );
          }}
        </StyledAssetContainer>
      </StyledWrapper>
      {showFilterPills && <StyleGuideFilterPills />}
    </>
  );
}

StyleGuide.propTypes = {
  containerName: PropTypes.string,
  leadAsset: PropTypes.string,
};
